// SAFE MODE: delivery sessions improvements
// Client service for the external delivery page (OTP gate + gallery + ZIP download)

const BASE = (import.meta.env?.VITE_SUPABASE_URL ?? "").replace(/\/$/, "");
const ANON = import.meta.env?.VITE_SUPABASE_ANON_KEY ?? "";

const headers = (extra = {}) => ({
  "Content-Type": "application/json",
  "apikey": ANON,
  ...extra,
});

const call = (fn, body, authToken) =>
  fetch(`${BASE}/functions/v1/${fn}`, {
    method: "POST",
    headers: headers(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    body: JSON.stringify(body),
  }).then(r => r.json());

/**
 * Create a 7-day delivery link. Called from MyDeliveries when user presses
 * "تحميل الألبوم كاملاً". Requires the authenticated user's access token.
 */
export const createDeliveryLink = (deliveryId, accessToken) =>
  call("create-delivery-link", { delivery_id: deliveryId }, accessToken);

/**
 * Validate entered phone against the customer's DB phone, then send OTP via SMS.
 * @param {string} token - delivery JWT token from the URL
 * @param {string} phone - phone number entered by user
 */
export const sendDeliveryOtp = (token, phone) =>
  call("send-delivery-otp", { token, phone });

/**
 * Verify the OTP. On success returns { ok: true, access_token }.
 * msegatId: the id returned from sendDeliveryOtp response.
 */
export const verifyDeliveryOtp = (token, phone, otpCode, msegatId) =>
  call("verify-delivery-otp", { token, phone, otp_code: otpCode, msegatId });

/**
 * Load delivery metadata + thumbnail URLs. Requires access_token.
 */
export const getDeliveryFiles = (accessToken) =>
  call("get-delivery-files", { access_token: accessToken });

/**
 * Signed URLs for original files (client-side ZIP). type: 'all' | 'photos' | 'videos'
 */
export const getDeliveryDownloadUrls = (accessToken, type = "all") =>
  call("get-delivery-download-urls", { access_token: accessToken, type });

/**
 * Submit a star rating for the photographer after viewing the delivery.
 * Uses the OTP access_token to authenticate (no Supabase auth needed).
 */
export const submitDeliveryRating = (accessToken, rating, comment = "") =>
  call("submit-delivery-rating", { access_token: accessToken, rating, comment });

/**
 * Submit a delivery dispute report.
 * Uses the OTP access_token — no Supabase auth session needed.
 * @param {string} accessToken - OTP JWT
 * @param {string} disputeType - 'missing_photos' | 'low_quality' | 'wrong_editing' | 'other'
 * @param {string} message - customer's description of the issue
 */
export const submitDeliveryDispute = (accessToken, disputeType, message) =>
  call("submit-delivery-dispute", { access_token: accessToken, dispute_type: disputeType, message });

/** @deprecated Server-side ZIP — prefer client ZIP via getDeliveryDownloadUrls + buildDeliveryZipBlob */
export async function downloadDeliveryZip(accessToken, type = "all") {
  const res = await fetch(`${BASE}/functions/v1/download-delivery-zip`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ access_token: accessToken, type }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    return { ok: false, message: err?.message || "فشل التحميل" };
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  return { ok: true, blob, fileName: match?.[1] ?? "album.zip" };
}
